import { Alert } from '@mui/material';
import { Box } from '@mui/system';
import type { useFormik } from 'formik';
import type { TFunction } from 'i18next';
import type { ComponentProps } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { DetailTextField } from '~/components/forms/details';
import {
  SettingDescription,
  SettingFieldRequired,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
  SettingsIntro,
} from '~/components/settings';

export interface TVBrandFormValues {
  baudRate?: string;
  input1?: string;
  input2?: string;
  input3?: string;
  mute?: string;
  name: string;
  powerOff?: string;
  powerOn?: string;
  status?: string;
  unmute?: string;
  volumeDown?: string;
  volumeUp?: string;
}

export const getValidationSchema = (t: TFunction) =>
  object({
    baudRate: string()
      .optional()
      .label(t('pages.tv_brands.detail.form.labels.baud_rate'))
      .test(
        'is-valid-number',
        t('pages.tv_brands.detail.form.validation.baud_rate_invalid'),
        (value) => {
          if (!value) return true; // Allow empty value as optional
          const numberValue = Number(value);
          return Number.isInteger(numberValue) && numberValue >= 0 && numberValue <= 2147483647; // 32-bit signed integer max
        },
      ),
    input1: string().optional().label(t('pages.tv_brands.detail.form.labels.input1')),
    input2: string().optional().label(t('pages.tv_brands.detail.form.labels.input2')),
    input3: string().optional().label(t('pages.tv_brands.detail.form.labels.input3')),
    mute: string().optional().label(t('pages.tv_brands.detail.form.labels.mute')),
    name: string()
      .required(t('pages.tv_brands.detail.form.validation.name_required'))
      .label(t('common.labels.name')),
    powerOff: string().optional().label(t('pages.tv_brands.detail.form.labels.power_off')),
    powerOn: string().optional().label(t('pages.tv_brands.detail.form.labels.power_on')),
    status: string().optional().label(t('common.labels.status')),
    unmute: string().optional().label(t('pages.tv_brands.detail.form.labels.unmute')),
    volumeDown: string().optional().label(t('pages.tv_brands.detail.form.labels.volume_down')),
    volumeUp: string().optional().label(t('pages.tv_brands.detail.form.labels.volume_up')),
  });

export interface TVBrandFormProps<T extends TVBrandFormValues>
  extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  formik: ReturnType<typeof useFormik<T>>;
  formId: string;
  readonly?: boolean;
}

export const TVBrandForm = <T extends TVBrandFormValues>({
  formik,
  formId,
  readonly = false,
}: TVBrandFormProps<T>) => {
  const { t } = useTranslation();

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={formik.handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <div>
        <SettingsHead>{t('common.labels.general')}</SettingsHead>
        <SettingsBody>
          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.name')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.name')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                autoFocus
                disabled={readonly || formik.isSubmitting}
                error={formik.touched.name && !!formik.errors.name}
                fullWidth
                helperText={(formik.touched.name && formik.errors.name) || ' '}
                name="name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </div>
      <div>
        <SettingsHead>{t('pages.tv_brands.detail.form.sections.rs232')}</SettingsHead>

        <SettingsIntro>
          <Alert severity="info">
            <Trans
              i18nKey="pages.tv_brands.detail.form.alert"
              components={{
                code: <code />,
                0: <>{'<'}</>,
                1: <>{'>'}</>,
                2: <>{'<'}</>,
                3: <>{'>'}</>,
                4: <>{'<'}</>,
                5: <>{'>'}</>,
              }}
            />
          </Alert>
        </SettingsIntro>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.baud_rate')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.baud_rate')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.baudRate}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.baudRate ?? ' '}
                name="baudRate"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.baudRate}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.input1')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.input1')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.input1}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.input1 ?? ' '}
                name="input1"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.input1}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.input2')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.input2')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.input2}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.input2 ?? ' '}
                name="input2"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.input2}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.input3')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.input3')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.input3}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.input3 ?? ' '}
                name="input3"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.input3}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.mute')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.mute')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.mute}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.mute ?? ' '}
                name="mute"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.mute}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.unmute')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.unmute')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.unmute}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.unmute ?? ' '}
                name="unmute"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.unmute}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.power_off')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.power_off')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.powerOff}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.powerOff ?? ' '}
                name="powerOff"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.powerOff}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.power_on')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.power_on')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.powerOn}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.powerOn ?? ' '}
                name="powerOn"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.powerOn}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('common.labels.status')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.status')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.status}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.status ?? ' '}
                name="status"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.status}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.volume_up')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.volume_up')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.volumeUp}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.volumeUp ?? ' '}
                name="volumeUp"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.volumeUp}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>{t('pages.tv_brands.detail.form.labels.volume_down')}</SettingName>
              <SettingDescription>
                {t('pages.tv_brands.detail.form.descriptions.volume_down')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                error={!!formik.errors.volumeDown}
                disabled={readonly || formik.isSubmitting}
                fullWidth
                helperText={formik.errors.volumeDown ?? ' '}
                name="volumeDown"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.volumeDown}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </div>
    </Box>
  );
};
